/**
 * `compile(source)` : lexer → parseur → analyseur. Point d'entrée unique de la compilation.
 * Jamais d'exception vers l'appelant : tout revient sous forme de `CompileResult`.
 */

import type { Program } from './ast';
import { SimCompileError, SimError } from './errors';
import { tokenize } from './lexer';
import { parse, MAX_REPORTED_ERRORS } from './parser';
import { analyze } from './analyzer';

export type CompileResult =
  | { ok: true; program: Program; warnings: string[] }
  | { ok: false; errors: SimError[]; warnings: string[] };

export function compile(source: string): CompileResult {
  let tokens;
  try {
    tokens = tokenize(source);
  } catch (e) {
    if (e instanceof SimCompileError) return { ok: false, errors: [e.error], warnings: [] };
    throw e;
  }

  const parsed = parse(tokens);
  const fatal = parsed.errors.some((err) => err.kind === 'syntax' && !err.hint);
  if (fatal || parsed.errors.length >= MAX_REPORTED_ERRORS) {
    return { ok: false, errors: finalize(parsed.errors), warnings: [] };
  }

  // Le parseur n'a rencontré que des constructions hors périmètre (sautées) : l'AST est complet,
  // on poursuit avec l'analyseur pour remonter le maximum d'erreurs distinctes en une passe.
  const analyzed = analyze(parsed.program);
  const errors = finalize([...parsed.errors, ...analyzed.errors]);
  if (errors.length > 0) return { ok: false, errors, warnings: analyzed.warnings };
  return { ok: true, program: parsed.program, warnings: analyzed.warnings };
}

function finalize(errors: SimError[]): SimError[] {
  const seen = new Set<string>();
  const unique = errors.filter((err) => {
    const key = `${err.kind}|${err.line}|${err.column ?? ''}|${err.symbol ?? err.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  unique.sort((a, b) => a.line - b.line || (a.column ?? 0) - (b.column ?? 0));
  return unique.slice(0, MAX_REPORTED_ERRORS);
}
